import { AfterViewInit, Component, OnInit } from "@angular/core";
import { Router } from "@angular/router";
import * as moment from "moment";
import { marketCategoryLabel } from "src/_constants/market_categories";
import { AppService } from "src/_services/app.service";

interface LeakLedgerStatsRow {
  date?: string;
  category: string;
  srBets: number;
  srStake: number;
  srGgr: number;
  gpBets: number;
  gpStake: number;
  gpGgr: number;
  leakPct: number;
  srExclusiveEvents?: number;
  srExclusiveBets?: number;
  srExclusiveStake?: number;
  srExclusiveGgr?: number;
  srPlusGenieEvents?: number;
  generatedAt?: number;
}

interface LeakLedgerEventMarketRow {
  eventId: string;
  eventName: string;
  group: string;
  category: string;
  marketName: string;
  marketNameDisplay: string;
  isMapped: boolean;
  mappedAt?: number;
  // True when SportRadar was already taking bets on this market before GeniePremium mapped it
  // that day -- i.e. the leak isn't (only) explained by the market never being mapped, GP was
  // just slow to turn it on. Only meaningful when isMapped is true.
  isMappingDelayed?: boolean;
  // Fixture start (ms) and each provider's betting window on this market (ms). All optional --
  // rows rolled up before these fields existed simply won't carry them, so every consumer below
  // has to treat 0/undefined as "unknown" rather than as an instant at the epoch.
  eventOpenDate?: number;
  srFirstBetAt?: number;
  srLastBetAt?: number;
  gpFirstBetAt?: number;
  srBets: number;
  srStake: number;
  srGgr: number;
  gpBets: number;
  gpStake: number;
  gpGgr: number;
  leakPct?: number;
  srBetsBeforeMapping?: number;
  srStakeBeforeMapping?: number;
  // Derived client-side once at fetch time (like leakPct), not returned by the API: how long SR
  // had the market before GP mapped it, and whether SR was still taking bets after that.
  mappingDelayMs?: number;
  // Prebuilt "138 before / 21 after mapping" label and its tooltip, null when the split doesn't
  // apply (market never mapped) or isn't known (row rolled up before the split existed).
  splitText?: string;
  splitTitle?: string;
  mappingDelayText?: string;
  leakedAfterMapping?: boolean;
}

interface Totals {
  srBets: number;
  srStake: number;
  srGgr: number;
  gpBets: number;
  gpStake: number;
  gpGgr: number;
  leakPct: number;
}

interface EventGroup extends Totals {
  eventId: string;
  eventName: string;
  group: string;
  markets: LeakLedgerEventMarketRow[];
  expanded: boolean;
  // Event-level rollup of the market rows below it: when the fixture was scheduled to start, and
  // when each provider first took a bet on ANY of its markets. Derived here rather than stored,
  // so it always reflects the markets currently passing the mapped/search filters.
  eventOpenDate: number;
  srFirstBetAt: number;
  gpFirstBetAt: number;
  // Signed gap between the scheduled start and the first SR bet on this event; negative means the
  // bet landed before the fixture was due to start. Null when either end is unknown.
  srVsStartMs: number;
  // Prebuilt label ("12m pre-match" / "5m in-play"), null when the gap can't be computed. Built
  // here rather than in the template: a `<` inside an interpolation confuses Angular's HTML
  // tokenizer, and a method call in a binding re-runs on every change-detection cycle.
  srVsStartText: string;
  srVsStartIsPre: boolean;
}

interface CategoryGroup extends Totals {
  category: string;
  events: EventGroup[];
  // Full, unfiltered rows for this category, fetched once on first expand. Re-filtering
  // (mappedFilter/search) recomputes `events` from this cache without another round-trip.
  rawRows: LeakLedgerEventMarketRow[];
  loaded: boolean;
  loading: boolean;
  eventCount: number;
  marketCount: number;
  expanded: boolean;
}

type MappedFilter = "all" | "mapped" | "gaps";

// Which metric the category/event header rows show. "all" shows all three side by side; the
// single-metric modes keep those rows scannable, which is the point of the drill-down summary.
type MetricMode = "bets" | "stake" | "ggr" | "all";

// SR = "leaked" (SportRadar took the bet), GP = "captured" (GeniePremium took the bet). Blue/
// orange is a colorblind-safe pair (Wong palette), used consistently across every chart on this
// page so the same color always means the same provider, in both light and dark mode.
const COLOR_GP = "#0072B2";
const COLOR_SR = "#E69F00";
const COLOR_TREND = "#6C5CE7";
const DARK_MODE_KEY = "ll-dark-mode";

const GROUP_LABELS: { [key: string]: string } = {
  winnerOnly: "Winner only mapped",
  winnerPlusOther: "Winner + other mapped",
  winnerNotMapped: "Winner not mapped",
};

@Component({
  selector: "app-leak-ledger",
  templateUrl: "./leak-ledger.component.html",
  styleUrls: ["./leak-ledger.component.scss"],
})
export class LeakLedgerComponent implements OnInit, AfterViewInit {
  loadingSummary = false;
  loadingCategories = false;

  darkMode = localStorage.getItem(DARK_MODE_KEY) === "true";

  selected: any = {
    startDate: moment().subtract(8, "days").startOf("day"),
    endDate: moment().subtract(2, "days").endOf("day"),
  };
  ranges: any = {
    "Last 7 Days": [
      moment().subtract(8, "days").startOf("day"),
      moment().subtract(2, "days").endOf("day"),
    ],
    "Last 14 Days": [
      moment().subtract(15, "days").startOf("day"),
      moment().subtract(2, "days").endOf("day"),
    ],
    "Last 30 Days": [
      moment().subtract(31, "days").startOf("day"),
      moment().subtract(2, "days").endOf("day"),
    ],
    "This Month": [
      moment().startOf("month"),
      moment().subtract(2, "days").endOf("day"),
    ],
    "Last Month": [
      moment().subtract(1, "month").startOf("month"),
      moment().subtract(1, "month").endOf("month"),
    ],
  };

  groupOptions = [
    { id: "", name: "All groups" },
    { id: "winnerOnly", name: "Winner only mapped" },
    { id: "winnerPlusOther", name: "Winner + other mapped" },
    { id: "winnerNotMapped", name: "Winner not mapped" },
  ];
  selectedGroup = "";
  mappedFilter: MappedFilter = "all";
  metricMode: MetricMode = "bets";
  searchTerm = "";

  totals: LeakLedgerStatsRow = null;
  dailyRows: LeakLedgerStatsRow[] = [];
  categoryRows: LeakLedgerStatsRow[] = [];
  // Totals for the CURRENTLY SELECTED group only -- derived client-side from dailyRows (already
  // group-scoped by the backend when selectedGroup is set, see loadSummary), not a separate
  // fetch. `totals` above stays the constant full-book reference; this is the "what does the
  // segment I've filtered to actually add up to" number the KPI cards can't show, since a
  // coverage-group total wouldn't make sense applied to them (SR-exclusive events aren't part of
  // the group taxonomy at all).
  scopedTotals: Totals = null;

  groupedRows: CategoryGroup[] = [];

  trendChart: any;
  volumeChart: any;
  categoryChart: any;

  showRecomputePanel = false;
  recomputeDateStr: string = null;
  recomputing = false;

  constructor(private appService: AppService, private router: Router) {
    this.initCharts();
  }

  ngOnInit() {
    this.loadAll();
  }

  ngAfterViewInit() {}

  get rangeSeconds() {
    return {
      startTime: Math.floor(new Date(this.selected.startDate).getTime() / 1000),
      endTime: Math.floor(new Date(this.selected.endDate).getTime() / 1000),
    };
  }

  // "SR leak" is SR activity on events GeniePremium DOES have -- SR total minus whatever happened
  // on events GP never knew about. Shown beside SR total on the Bets/Stake/GGR tiles so the two
  // read as one decomposition: everything SportRadar took, and the part of it GP was in a position
  // to have captured.
  //
  // Derived here rather than stored, and safe over ANY date range: every operand is a per-day SUM
  // and each bet belongs to exactly one day. That is not true of the SR-Exclusive EVENT count next
  // to them, which sums per-day distinct counts and so double-counts a fixture running past IST
  // midnight -- do not "fix" these into event counts.
  //
  // Deliberately all-categories, so these will NOT tie to the drill-down further down the page:
  // that is scoped to the hardcoded addressable categories, while this counts every category on a
  // GP-known event. The gap is real and runs to a few thousand bets a day.
  // Written out per metric rather than via a shared string-keyed helper: indexing the totals by
  // field name compiles even when the name is wrong, so a typo would silently render 0 instead of
  // failing the build. These are numbers people will act on, so the compiler should check them.
  get srLeakBets(): number {
    if (!this.totals) {
      return 0;
    }
    return (this.totals.srBets || 0) - (this.totals.srExclusiveBets || 0);
  }

  get srLeakStake(): number {
    if (!this.totals) {
      return 0;
    }
    return (this.totals.srStake || 0) - (this.totals.srExclusiveStake || 0);
  }

  get srLeakGgr(): number {
    if (!this.totals) {
      return 0;
    }
    return (this.totals.srGgr || 0) - (this.totals.srExclusiveGgr || 0);
  }

  categoryLabel(code: string): string {
    if (code === "ALL") {
      return "All categories";
    }
    return marketCategoryLabel(code);
  }

  groupLabel(code: string): string {
    return GROUP_LABELS[code] || code || "—";
  }

  leakSeverityClass(pct: number): string {
    if (pct === null || pct === undefined) {
      return "";
    }
    if (pct >= 50) {
      return "ll-severity-critical";
    }
    if (pct >= 20) {
      return "ll-severity-warning";
    }
    return "ll-severity-good";
  }

  // ---------- Dark mode ----------
  toggleDarkMode() {
    this.darkMode = !this.darkMode;
    localStorage.setItem(DARK_MODE_KEY, String(this.darkMode));
    this.trendChart = this.applyChartTheme(this.trendChart);
    this.volumeChart = this.applyChartTheme(this.volumeChart);
    this.categoryChart = this.applyChartTheme(this.categoryChart);
  }

  private themeTokens() {
    return this.darkMode
      ? { fg: "#9AA6B8", grid: "#2A2F3D", tooltipTheme: "dark" }
      : { fg: "#697483", grid: "#eef0f4", tooltipTheme: "light" };
  }

  private applyChartTheme(cfg: any): any {
    const t = this.themeTokens();
    return {
      ...cfg,
      chart: { ...cfg.chart, foreColor: t.fg, background: "transparent" },
      grid: { ...cfg.grid, borderColor: t.grid },
      tooltip: { ...cfg.tooltip, theme: t.tooltipTheme },
    };
  }

  // ---------- Filters ----------
  onDateChangeHandler = (event: any) => {
    this.selected = event;
    if (this.selected.endDate) {
      this.loadAll();
    }
  };

  onGroupChange() {
    // Reload the category chart/bars under the new group scope -- this replaces the whole
    // groupedRows skeleton with fresh per-category totals, so no manual cache-clearing needed
    // for it. The drill-down's per-category row cache is separate (fetched from the same
    // group-scoped endpoint but at market granularity) and needs its own invalidation: any
    // category already loaded was fetched under the OLD group and is now stale. Categories that
    // were open refetch immediately, closed ones refetch lazily on next expand.
    const previouslyLoaded = this.groupedRows.filter((cat) => cat.loaded && cat.expanded).map((cat) => cat.category);
    this.loadCategoryBreakdown(previouslyLoaded);
    // Trend/volume charts are also group-scoped (see loadSummary) -- refetch them too. The KPI
    // cards above stay put: loadSummary's `totals` always comes from the unscoped full total,
    // only its `daily` (chart-only) rows change with the group.
    this.loadSummary();
  }

  setMappedFilter(filter: MappedFilter) {
    this.mappedFilter = filter;
    this.recomputeLoadedCategories();
  }

  applyEventMarketTextFilter(value: string) {
    this.searchTerm = value || "";
    this.recomputeLoadedCategories();
  }

  private recomputeLoadedCategories() {
    // mappedFilter/search are purely client-side over what's already cached -- no re-fetch.
    // Categories never expanded yet simply pick up the current filters whenever they're loaded.
    this.groupedRows.filter((cat) => cat.loaded).forEach((cat) => this.recomputeCategoryEvents(cat));
  }

  toggleCategory(cat: CategoryGroup) {
    cat.expanded = !cat.expanded;
    if (cat.expanded) {
      this.ensureCategoryLoaded(cat);
    }
  }

  expandAllCategories() {
    // An explicit, deliberate "fetch everything" action -- up to ~14 parallel requests, one per
    // category. Only expands categories, not every event inside them, to keep the render sane.
    this.groupedRows.forEach((cat) => {
      cat.expanded = true;
      this.ensureCategoryLoaded(cat);
    });
  }

  collapseAll() {
    this.groupedRows.forEach((cat) => {
      cat.expanded = false;
      cat.events.forEach((ev) => (ev.expanded = false));
    });
  }

  // ---------- Data loading ----------
  loadAll() {
    this.loadSummary();
    this.loadCategoryBreakdown();
  }

  loadSummary() {
    this.loadingSummary = true;
    try {
      const filters: any = { ...this.rangeSeconds };
      if (this.selectedGroup) {
        filters.group = this.selectedGroup;
      }
      this.appService.getLeakLedgerSummary(filters).subscribe(
        (res) => {
          this.loadingSummary = false;
          if (res.status === "RS_OK") {
            this.totals = res.totals;
            this.dailyRows = res.daily || [];
            this.scopedTotals = this.selectedGroup ? this.sumTotals(this.dailyRows) : null;
            this.updateTrendChart();
            this.updateVolumeChart();
          } else {
            this.handleFailure(res);
          }
        },
        () => (this.loadingSummary = false)
      );
    } catch (err) {
      this.loadingSummary = false;
    }
  }

  // `reopenCategories`: category codes to immediately re-expand + refetch once the fresh
  // skeleton lands -- used by onGroupChange so switching the coverage-group filter doesn't
  // silently collapse panels the user already had open.
  loadCategoryBreakdown(reopenCategories: string[] = []) {
    this.loadingCategories = true;
    try {
      const filters: any = { ...this.rangeSeconds };
      if (this.selectedGroup) {
        filters.group = this.selectedGroup;
      }
      this.appService.getLeakLedgerCategoryBreakdown(filters).subscribe(
        (res) => {
          this.loadingCategories = false;
          if (res.status === "RS_OK") {
            this.categoryRows = (res.rows || [])
              .slice()
              .sort((a: LeakLedgerStatsRow, b: LeakLedgerStatsRow) => b.srStake - a.srStake);
            this.updateCategoryChart();
            // The drill-down's category shells come from this same call -- no per-market data
            // is fetched until a category is actually expanded (see ensureCategoryLoaded). A
            // 30-day range costs exactly what a 1-day range costs here. When `selectedGroup` is
            // set, both this chart and the drill-down are scoped to events GeniePremium
            // actually has at that coverage tier -- SR-exclusive events (GP doesn't know about
            // them at all) drop out entirely, which is deliberate: see the "Segment by mapping
            // coverage" hint in the template.
            this.groupedRows = this.categoryRows.map((r) => ({
              category: r.category,
              srBets: r.srBets,
              srStake: r.srStake,
              srGgr: r.srGgr,
              gpBets: r.gpBets,
              gpStake: r.gpStake,
              gpGgr: r.gpGgr,
              leakPct: r.leakPct,
              events: [],
              rawRows: [],
              loaded: false,
              loading: false,
              eventCount: 0,
              marketCount: 0,
              expanded: reopenCategories.includes(r.category),
            }));
            this.groupedRows.filter((cat) => cat.expanded).forEach((cat) => this.ensureCategoryLoaded(cat));
          } else {
            this.handleFailure(res);
          }
        },
        () => (this.loadingCategories = false)
      );
    } catch (err) {
      this.loadingCategories = false;
    }
  }

  // ---------- Category -> Event -> Market grouping (fetched lazily, one category at a time) ----------
  ensureCategoryLoaded(cat: CategoryGroup) {
    if (cat.loaded || cat.loading) {
      return;
    }
    cat.loading = true;
    const filters: any = { ...this.rangeSeconds, category: cat.category };
    if (this.selectedGroup) {
      filters.group = this.selectedGroup;
    }
    try {
      this.appService.getLeakLedgerEventMarkets(filters).subscribe(
        (res) => {
          cat.loading = false;
          if (res.status === "RS_OK") {
            cat.rawRows = (res.rows || []).map((r: LeakLedgerEventMarketRow) => ({
              ...r,
              leakPct: r.srBets + r.gpBets > 0 ? (r.srBets / (r.srBets + r.gpBets)) * 100 : 0,
              mappingDelayMs: this.mappingDelayMs(r),
              mappingDelayText: this.formatDuration(this.mappingDelayMs(r)),
              leakedAfterMapping: this.leakedAfterMapping(r),
              splitText: this.splitText(r),
              splitTitle: this.splitTitle(r),
            }));
            cat.loaded = true;
            this.recomputeCategoryEvents(cat);
          } else {
            this.handleFailure(res);
          }
        },
        () => (cat.loading = false)
      );
    } catch (err) {
      cat.loading = false;
    }
  }

  private sumTotals(
    items: Array<{ srBets: number; srStake: number; srGgr: number; gpBets: number; gpStake: number; gpGgr: number }>
  ): Totals {
    const t = items.reduce<Totals>(
      (acc, r) => {
        acc.srBets += r.srBets;
        acc.srStake += r.srStake;
        acc.srGgr += r.srGgr;
        acc.gpBets += r.gpBets;
        acc.gpStake += r.gpStake;
        acc.gpGgr += r.gpGgr;
        return acc;
      },
      { srBets: 0, srStake: 0, srGgr: 0, gpBets: 0, gpStake: 0, gpGgr: 0, leakPct: 0 }
    );
    const sum = t.srBets + t.gpBets;
    t.leakPct = sum > 0 ? (t.srBets / sum) * 100 : 0;
    return t;
  }

  setMetricMode(mode: MetricMode) {
    // Pure display switch -- every value is already on the row objects, so nothing refetches and
    // nothing regroups. Categories that have not been expanded stay unloaded.
    this.metricMode = mode;
  }

  // Leak % for a header row, on the basis of whatever metric is being shown.
  //
  // Bets and stake are both non-negative sums, so srX / (srX + gpX) is meaningful for each. GGR is
  // NOT: it is signed (a losing book gives a negative), so that ratio can exceed 100%, flip sign,
  // or divide by a near-zero sum. In GGR and "all" modes the pill therefore stays on the bets
  // basis, which is what "leak rate" means everywhere else in this report.
  rowLeakPct(r: Totals): number {
    if (this.metricMode === "stake") {
      const sum = (r.srStake || 0) + (r.gpStake || 0);
      return sum > 0 ? ((r.srStake || 0) / sum) * 100 : 0;
    }
    return r.leakPct || 0;
  }

  // Label under the pill so it is never ambiguous which basis is on screen.
  get leakPctBasis(): string {
    return this.metricMode === "stake" ? "stake" : "bets";
  }

  private minNonZero(values: number[]): number {
    return values.reduce((min, v) => (v > 0 && (min === 0 || v < min) ? v : min), 0);
  }

  // Formats a signed millisecond gap as a compact human duration ("47m", "3h 12m", "2d 4h").
  // Sub-minute gaps collapse to "<1m" rather than showing seconds -- premium_markets.created_at
  // only has second resolution, so finer precision than that would be false confidence.
  formatDuration(ms: number): string {
    if (!ms) {
      return null;
    }
    const abs = Math.abs(ms);
    const mins = Math.floor(abs / 60000);
    if (mins < 1) {
      return "<1m";
    }
    if (mins < 60) {
      return `${mins}m`;
    }
    const hours = Math.floor(mins / 60);
    const remMins = mins % 60;
    if (hours < 24) {
      return remMins ? `${hours}h ${remMins}m` : `${hours}h`;
    }
    const days = Math.floor(hours / 24);
    const remHours = hours % 24;
    return remHours ? `${days}d ${remHours}h` : `${days}d`;
  }

  // How long SportRadar had this market to itself before GeniePremium mapped it. Null unless both
  // ends are known AND mapping genuinely came second -- a market mapped before its first SR bet
  // has no delay to report, and saying "0m" there would read as a measurement rather than as the
  // absence of a problem.
  mappingDelayMs(m: LeakLedgerEventMarketRow): number {
    if (!m.isMapped || !m.mappedAt || !m.srFirstBetAt || m.mappedAt <= m.srFirstBetAt) {
      return null;
    }
    return m.mappedAt - m.srFirstBetAt;
  }

  // Whether SR kept betting on this market AFTER it was mapped. Distinguishes "GP was just late"
  // (all SR bets land before mappedAt) from "GP was mapped and STILL lost the bet" -- the second
  // is a pricing/limits/latency problem no amount of faster mapping would fix.
  leakedAfterMapping(m: LeakLedgerEventMarketRow): boolean {
    return !!(m.isMapped && m.mappedAt && m.srLastBetAt && m.srLastBetAt > m.mappedAt);
  }

  // "138 before / 21 after mapping" -- how the market's SR bets fall either side of the moment
  // GeniePremium mapped it. Null when the market was never mapped (nothing to split on, and the
  // Gap pill already says so) or when it has no SR bets.
  //
  // A mapped row whose srBetsBeforeMapping is 0 is genuinely "all after mapping", not "unknown":
  // the backend resolves that case from timestamps without needing the per-second tally. Rows
  // predating the split field entirely are caught by the srFirstBetAt check -- those rows have no
  // timestamps at all, so nothing here can be trusted for them.
  splitText(m: LeakLedgerEventMarketRow): string {
    if (!m.isMapped || !m.mappedAt || !m.srBets || !m.srFirstBetAt) {
      return null;
    }
    const before = m.srBetsBeforeMapping || 0;
    // A market whose SR betting spans the mapping instant MUST have at least one bet before it --
    // srFirstBetAt is itself an SR bet and it precedes mappedAt. So a 0 on a straddling row means
    // the backend's per-second tally didn't run or failed, NOT that nothing preceded mapping.
    // Render nothing rather than a confident "0 before".
    const straddles = m.srFirstBetAt < m.mappedAt && m.srLastBetAt >= m.mappedAt;
    if (straddles && before === 0) {
      return null;
    }
    // Defensive: "before" is a subset of the same bets, so it can never exceed the total. If a
    // partially-backfilled range ever made it look otherwise, show nothing instead of a negative.
    if (before > m.srBets) {
      return null;
    }
    const after = m.srBets - before;
    return `${before.toLocaleString()} before / ${after.toLocaleString()} after mapping`;
  }

  splitTitle(m: LeakLedgerEventMarketRow): string {
    if (!this.splitText(m)) {
      return null;
    }
    const before = m.srBetsBeforeMapping || 0;
    const stake = Math.round(m.srStakeBeforeMapping || 0);
    return (
      `Of ${m.srBets.toLocaleString()} SportRadar bets on this market, ` +
      `${before.toLocaleString()} (${stake.toLocaleString()} stake) landed before GeniePremium mapped it.`
    );
  }

  // Signed gap between the fixture's scheduled start and the first SR bet, plus its prebuilt
  // label. Negative = placed before the scheduled start, i.e. pre-match. All three fields go null
  // together when either end is unknown, so the template can gate on the label alone.
  //
  // Worth reading as "pre-match relative to SR's scheduled start", not "pre-match in the catalog
  // sense": every bet in this report is already filtered to c_id LIVE, so this says the bet landed
  // before the advertised start time, not that it was placed off a pre-match catalog.
  private startGapFields(firstBetAt: number, openDate: number) {
    if (!firstBetAt || !openDate) {
      return { srVsStartMs: null, srVsStartText: null, srVsStartIsPre: false };
    }
    const gap = firstBetAt - openDate;
    return {
      srVsStartMs: gap,
      srVsStartText: `${this.formatDuration(gap)} ${gap < 0 ? "pre-match" : "in-play"}`,
      srVsStartIsPre: gap < 0,
    };
  }

  // Groups one category's cached rawRows into events, applying the current mappedFilter/search
  // -- purely client-side, no network call, so toggling a filter is instant for loaded categories.
  private recomputeCategoryEvents(cat: CategoryGroup) {
    const prevEvExpanded = new Map(cat.events.map((e): [string, boolean] => [e.eventId, e.expanded]));

    let rows = cat.rawRows;
    if (this.mappedFilter === "mapped") {
      rows = rows.filter((r) => r.isMapped);
    } else if (this.mappedFilter === "gaps") {
      rows = rows.filter((r) => !r.isMapped && r.srBets > 0);
    }
    if (this.searchTerm.trim()) {
      const term = this.searchTerm.trim().toLowerCase();
      rows = rows.filter(
        (r) =>
          (r.eventName || "").toLowerCase().includes(term) ||
          (r.marketNameDisplay || "").toLowerCase().includes(term)
      );
    }

    const evMap = new Map<string, LeakLedgerEventMarketRow[]>();
    rows.forEach((r) => {
      if (!evMap.has(r.eventId)) {
        evMap.set(r.eventId, []);
      }
      evMap.get(r.eventId).push(r);
    });

    const events: EventGroup[] = [];
    evMap.forEach((markets, eventId) => {
      markets.sort((a, b) => b.srStake - a.srStake);
      const totals = this.sumTotals(markets);
      events.push({
        eventId,
        eventName: markets[0].eventName,
        group: markets[0].group,
        markets,
        expanded: prevEvExpanded.get(eventId) || false,
        // Earliest non-zero across the event's markets -- 0/undefined means "no bets from that
        // provider" (or a row predating these fields), never an instant at the epoch, so it must
        // not win a min(). Same rule the backend's cross-day merge follows.
        eventOpenDate: this.minNonZero(markets.map((m) => m.eventOpenDate)),
        srFirstBetAt: this.minNonZero(markets.map((m) => m.srFirstBetAt)),
        gpFirstBetAt: this.minNonZero(markets.map((m) => m.gpFirstBetAt)),
        ...this.startGapFields(
          this.minNonZero(markets.map((m) => m.srFirstBetAt)),
          this.minNonZero(markets.map((m) => m.eventOpenDate))
        ),
        ...totals,
      });
    });
    events.sort((a, b) => b.srStake - a.srStake);

    cat.events = events;
    cat.eventCount = events.length;
    cat.marketCount = events.reduce((s, e) => s + e.markets.length, 0);
  }

  handleFailure(res: any) {
    if (res && res.errorDescription === "Invalid Token!") {
      this.appService.openErrorBar(res.errorDescription);
      sessionStorage.clear();
      this.router.navigateByUrl("/login");
    }
  }

  // ---------- Manual recompute ----------
  toggleRecomputePanel() {
    this.showRecomputePanel = !this.showRecomputePanel;
  }

  triggerRecompute() {
    if (!this.recomputeDateStr || this.recomputing) {
      return;
    }
    this.recomputing = true;
    this.appService.triggerLeakLedgerBackfill({ date: this.recomputeDateStr }).subscribe(
      (res) => {
        this.recomputing = false;
        if (res.status === "RS_OK") {
          this.appService.openSuccessBar(
            `Recompute for ${this.recomputeDateStr} started — refresh in a minute to see it.`
          );
          this.showRecomputePanel = false;
        } else {
          this.handleFailure(res);
          this.appService.openErrorBar(res.errorDescription || "Failed to trigger recompute.");
        }
      },
      () => {
        this.recomputing = false;
        this.appService.openErrorBar("Failed to trigger recompute.");
      }
    );
  }

  // ---------- Charts ----------
  private initCharts() {
    const baseChart = {
      chart: {
        type: "area",
        height: 280,
        toolbar: { show: false },
        zoom: { enabled: false },
        fontFamily: "SF-Pro-Display-Regular",
      },
      dataLabels: { enabled: false },
      grid: { borderColor: "#eef0f4", strokeDashArray: 4 },
      tooltip: { theme: "light" },
      legend: { show: false },
    };

    this.trendChart = {
      ...baseChart,
      series: [{ name: "Leak %", data: [] }],
      chart: { ...baseChart.chart, type: "area" },
      colors: [COLOR_TREND],
      stroke: { curve: "smooth", width: 2.5 },
      fill: {
        type: "gradient",
        gradient: { shadeIntensity: 1, opacityFrom: 0.35, opacityTo: 0.02, stops: [0, 90, 100] },
      },
      markers: { size: 0, hover: { size: 5 } },
      xaxis: { type: "datetime", labels: { format: "dd MMM" } },
      yaxis: { min: 0, max: 100, labels: { formatter: (v: number) => `${Math.round(v)}%` } },
      tooltip: { theme: "light", x: { format: "dd MMM yyyy" }, y: { formatter: (v: number) => `${v.toFixed(1)}%` } },
    };

    this.volumeChart = {
      ...baseChart,
      series: [
        { name: "GeniePremium bets", data: [] },
        { name: "SportRadar bets", data: [] },
      ],
      chart: { ...baseChart.chart, type: "bar", stacked: false },
      colors: [COLOR_GP, COLOR_SR],
      plotOptions: { bar: { columnWidth: "55%", borderRadius: 3 } },
      stroke: { show: false, curve: "smooth", width: 0 },
      fill: { opacity: 1 },
      xaxis: { type: "datetime", labels: { format: "dd MMM" } },
      yaxis: { labels: { formatter: (v: number) => `${Math.round(v)}` } },
      legend: { show: true, position: "top", horizontalAlign: "right" },
      tooltip: { theme: "light", x: { format: "dd MMM yyyy" } },
    };

    this.categoryChart = {
      series: [
        { name: "GeniePremium bets", data: [] },
        { name: "SportRadar bets", data: [] },
      ],
      chart: { type: "bar", height: 360, toolbar: { show: false }, fontFamily: "SF-Pro-Display-Regular" },
      colors: [COLOR_GP, COLOR_SR],
      plotOptions: { bar: { horizontal: true, barHeight: "60%", borderRadius: 3 } },
      dataLabels: { enabled: false },
      grid: { borderColor: "#eef0f4", strokeDashArray: 4 },
      xaxis: { categories: [] },
      legend: { show: true, position: "top", horizontalAlign: "right" },
      tooltip: { theme: "light" },
    };

    this.trendChart = this.applyChartTheme(this.trendChart);
    this.volumeChart = this.applyChartTheme(this.volumeChart);
    this.categoryChart = this.applyChartTheme(this.categoryChart);
  }

  private updateTrendChart() {
    const rows = this.dailyRows.slice().sort((a, b) => (a.date < b.date ? -1 : 1));
    const data = rows.map((r) => [moment.utc(r.date, "YYYY-MM-DD").valueOf(), Number(r.leakPct.toFixed(2))]);
    this.trendChart = { ...this.trendChart, series: [{ name: "Leak %", data }] };
  }

  private updateVolumeChart() {
    const rows = this.dailyRows.slice().sort((a, b) => (a.date < b.date ? -1 : 1));
    const gp = rows.map((r) => [moment.utc(r.date, "YYYY-MM-DD").valueOf(), r.gpBets]);
    const sr = rows.map((r) => [moment.utc(r.date, "YYYY-MM-DD").valueOf(), r.srBets]);
    this.volumeChart = {
      ...this.volumeChart,
      series: [
        { name: "GeniePremium bets", data: gp },
        { name: "SportRadar bets", data: sr },
      ],
    };
  }

  private updateCategoryChart() {
    const rows = this.categoryRows.slice(0, 14);
    this.categoryChart = {
      ...this.categoryChart,
      series: [
        { name: "GeniePremium bets", data: rows.map((r) => r.gpBets) },
        { name: "SportRadar bets", data: rows.map((r) => r.srBets) },
      ],
      xaxis: { categories: rows.map((r) => this.categoryLabel(r.category)) },
    };
  }
}
